import React, { useEffect, useRef } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { CircularProgress, Box } from '@mui/material';

const ProtectedRoute = ({ children, requiredPermission, requiredPermissionsAny, requiredRole, requireAdmin = false }) => {
  const { isAuthenticated, isLoading, user, hasPermission, hasAnyPermission, hasRole, isAdmin, syncSession } = useAuth();
  const location = useLocation();
  const hasTriedSyncRef = useRef(false);

  const needsPermissions = !!(requiredPermission || requiredPermissionsAny || requiredRole || requireAdmin);

  // Hooks devem ser chamados incondicionalmente.
  useEffect(() => {
    if (isLoading) return;
    if (!isAuthenticated) return;
    if (!needsPermissions) return;
    if (user && Array.isArray(user.permissions)) return;
    if (hasTriedSyncRef.current) return;

    const hasToken = !!localStorage.getItem('token');
    if (!hasToken) return;

    hasTriedSyncRef.current = true;
    syncSession({ silent: true });
  }, [isLoading, isAuthenticated, needsPermissions, syncSession, user]);

  // Mostrar loading enquanto verifica autenticação
  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        flexDirection="column"
      >
        <CircularProgress size={60} />
        <Box mt={2} color="text.secondary">
          Verificando autenticação...
        </Box>
      </Box>
    );
  }

  // Se não estiver autenticado, redirecionar para login
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (needsPermissions && (!user || !Array.isArray(user.permissions))) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        flexDirection="column"
      >
        <CircularProgress size={60} />
        <Box mt={2} color="text.secondary">
          Sincronizando sessão...
        </Box>
      </Box>
    );
  }

  // Verificar se é necessário ser admin
  if (requireAdmin && !isAdmin()) {
    return <Navigate to="/" replace />;
  }

  // Verificar permissão específica
  if (requiredPermission && !hasPermission(requiredPermission)) {
    return <Navigate to="/" replace />;
  }

  // Verificar conjunto de permissões alternativas (qualquer uma)
  if (requiredPermissionsAny && !hasAnyPermission(requiredPermissionsAny)) {
    return <Navigate to="/" replace />;
  }

  // Verificar papel/função específica
  if (requiredRole && !hasRole(requiredRole)) {
    return <Navigate to="/" replace />;
  }

  // Se estiver autenticado e autorizado, renderizar o componente
  return children;
};

export default ProtectedRoute;
